/**
 * Parses `@`-mentions inside XAES Analyst user input and resolves them
 * to compact text blocks that can be inlined into the system prompt as
 * `<reference>…</reference>` wrappers.
 *
 * Source references:
 *   - Loveable v1 reference: `src/lib/reference-resolver.ts` — same
 *     conceptual shape, but Phase 4 of the rebuild only needs the
 *     light text-only branch (no PDF document blocks, no GitHub fetch).
 *   - ADR 0001 — reference content is **untrusted user data**; the
 *     wrapper format `<reference name="…">…</reference>` matches the
 *     trust-boundary rules in `src/content/xaes-system-prompt.ts`.
 *
 * Reference kinds:
 *   - `@workshop/{id}`              workshop overview (title/status/brief/crew)
 *   - `@crew/{id}`                  crew roster with bios and observer distinction
 *   - `@phase/{id}/{n}`             1-based phase summary for the workshop
 *   - `@briefing/{id}`              pre-reading dossier content
 *   - `@questionnaire/{id}`         questionnaire questions + responses
 *   - `@output/{id}/{output}`       one of the AssetLocker output cards
 *                                   (transcript / analysis / data-tables /
 *                                   report / playback / captures)
 *   - `@asset/{id}/{uploadId}`      a curated upload from `useAssetLocker`
 *   - `@finding/{id}/{findingId}`   finding from the findings store
 *   - `@decision/{id}/{decisionId}` decision from the decisions store
 *   - `@action/{id}/{actionId}`       action from the actions store
 *   - `@assumption/{id}/{assumptionId}` assumption from the assumptions store
 *   - `@opportunity/{id}/{oppId}`     opportunity from the opportunities store
 *   - `@recommendation/{id}/{recId}`  recommendation from the recommendations store
 *   - `@note/{id}/{noteId}`           facilitation note from the notes store
 *   - `@transcript/{id}/{transcriptId}` transcript from the transcripts store
 */

import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { briefByWorkshop } from "@/features/workshop/content";
import { formatWorkshopCrew, resolveWorkshopCrew } from "./crew-context";
import {
  formatPreReading,
  formatQuestionnaire,
  getEffectiveWorkshopPhases,
  resolveOutputContent,
} from "./workshop-content-context";
import { useAssetLocker } from "@/stores/useAssetLocker";
import { useFindings } from "@/stores/useFindings";
import { useDecisions } from "@/stores/useDecisions";
import { useActions } from "@/stores/useActions";
import { useAssumptions } from "@/stores/useAssumptions";
import { useOpportunities } from "@/stores/useOpportunities";
import { useRecommendations } from "@/stores/useRecommendations";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";
import { useTranscripts } from "@/stores/useTranscripts";

/** Full body included in @-mention transclusion up to this length. */
const BODY_FULL_MAX = 2000;

function formatBodyContent(text: string): { body: string; truncated: boolean } {
  const trimmed = text.trim();
  if (trimmed.length <= BODY_FULL_MAX) {
    return { body: trimmed, truncated: false };
  }
  return {
    body: `${trimmed.slice(0, BODY_FULL_MAX).trim()}…`,
    truncated: true,
  };
}

/** Reference kinds the picker exposes. */
export type ReferenceKind =
  | "workshop"
  | "crew"
  | "phase"
  | "briefing"
  | "questionnaire"
  | "output"
  | "finding"
  | "decision"
  | "action"
  | "assumption"
  | "opportunity"
  | "recommendation"
  | "note"
  | "transcript"
  | "asset";

/** Canonical id for the six workshop output cards (matches
 *  `AssetLocker.OUTPUTS`). Keeping this here means the picker and the
 *  resolver agree on the same string namespace. */
export type WorkshopOutputId =
  | "transcript"
  | "analysis"
  | "data-tables"
  | "report"
  | "playback"
  | "captures";

const OUTPUT_LABELS: Record<WorkshopOutputId, string> = {
  transcript: "Transcript",
  analysis: "Analysis",
  "data-tables": "Data Tables",
  report: "Report",
  playback: "Playback",
  captures: "Captures",
};

const OUTPUT_DESCRIPTIONS: Record<WorkshopOutputId, string> = {
  transcript:
    "Live session transcript — speaker-attributed dialogue captured during the workshop.",
  analysis:
    "Post-session analytical write-up — themes, decisions, open questions, and recommended actions.",
  "data-tables":
    "Structured tables exported from the session — questionnaire results, voting tallies, scoring grids.",
  report:
    "Final workshop report — narrative document summarising outcomes, agreed designs, and next steps.",
  playback:
    "Session replay artefact — slide-by-slide playback of the workshop deck with annotations.",
  captures:
    "Visual captures — whiteboard photos, screen captures, and annotated diagrams produced during the workshop.",
};

/**
 * A user-attached reference, captured at compose time.
 *
 * `id`-style discriminators per kind:
 *   - `workshop` / `briefing`         → `workshopId` only.
 *   - `phase`                         → `workshopId` + `phase` (1-based).
 *   - `output`                        → `workshopId` + `outputId`.
 *   - `asset` / `finding` / `decision` / `action` / `assumption`
 *                                       → `workshopId` + `assetId` (the
 *                                       store id; `assetName` carries
 *                                       the display label so the chip
 *                                       can render without a store hit).
 *
 * Every workshop is attachable as context regardless of its
 * `Workshop.status` — locked/upcoming workshops are surfaced and
 * selectable just like done/active ones.
 */
export type XaesReference = {
  kind: ReferenceKind;
  /** Workshop id this reference belongs to. */
  workshopId: Workshop["id"];
  /** Optional 1-based phase index — only meaningful when `kind === "phase"`. */
  phase?: number;
  /** Output card id — only meaningful when `kind === "output"`. */
  outputId?: WorkshopOutputId;
  /** Store id — only meaningful for `asset` / register kinds. */
  assetId?: string;
  /** Cached label for register refs so chips render after a store reset. */
  assetName?: string;
  /** Display label — derived for picker UI, persisted with the reference. */
  label: string;
};

/** Single-line workshop title for chips / picker rows (strips navigator `\n`). */
export function workshopRefLabel(ws: Workshop): string {
  const wsTitle = ws.title.replace(/\s*\n\s*/g, " ");
  return `${ws.id} — ${wsTitle}`;
}

/**
 * Default compose-bar context tags for a chat scope. Workshop dashboards
 * seed the open workshop; Mission Control starts with an empty set so
 * the user opts in via `+ Add` / `@`.
 */
export function defaultContextReferences(
  scope: Workshop["id"] | "MC",
): XaesReference[] {
  if (scope === "MC") return [];
  const ws = WORKSHOPS.find((w) => w.id === scope);
  if (!ws) return [];
  return [
    {
      kind: "workshop",
      workshopId: ws.id,
      label: workshopRefLabel(ws),
    },
  ];
}

/** Resolved reference block ready to inline in the system prompt. */
export type ResolvedReference =
  | { kind: "text"; heading: string; content: string }
  | { kind: "metadata"; heading: string; note: string };

/**
 * Resolve a single reference into a content block. Async signature so
 * we can swap the phase stub for a real fetch later without changing
 * callers.
 */
export async function resolveReference(
  ref: XaesReference,
): Promise<ResolvedReference> {
  switch (ref.kind) {
    case "workshop": {
      const ws = WORKSHOPS.find((w) => w.id === ref.workshopId);
      if (!ws) {
        return {
          kind: "metadata",
          heading: `@workshop/${ref.workshopId}`,
          note: "(workshop not found)",
        };
      }
      const brief = briefByWorkshop[ws.id] ?? [];
      const crewMd = formatWorkshopCrew(ws.id);
      const briefMd = brief.length
        ? brief.map((b) => `- ${b.label}${b.meta ? ` _(${b.meta})_` : ""}`).join("\n")
        : "_(none)_";
      const content = [
        `### ${ws.longTitle}`,
        `- Status: **${ws.status}**`,
        `- Accent: \`${ws.accent}\``,
        ``,
        `**Brief**`,
        briefMd,
        ``,
        `**Crew**`,
        crewMd,
      ].join("\n");
      return {
        kind: "text",
        heading: `@workshop/${ws.id}`,
        content,
      };
    }
    case "crew": {
      const ws = WORKSHOPS.find((w) => w.id === ref.workshopId);
      if (!ws) {
        return {
          kind: "metadata",
          heading: `@crew/${ref.workshopId}`,
          note: "(workshop not found)",
        };
      }
      const crew = resolveWorkshopCrew(ws.id);
      if (crew.length === 0) {
        return {
          kind: "metadata",
          heading: `@crew/${ws.id}`,
          note: "(no crew members resolved for this workshop)",
        };
      }
      return {
        kind: "text",
        heading: `@crew/${ws.id}`,
        content: formatWorkshopCrew(ws.id),
      };
    }
    case "phase": {
      const n = ref.phase ?? 1;
      const phases = getEffectiveWorkshopPhases(ref.workshopId);
      const phase = phases[n - 1];
      if (!phase) {
        return {
          kind: "metadata",
          heading: `@phase/${ref.workshopId}/${n}`,
          note: "(phase not found)",
        };
      }
      const content = [
        `**Phase ${n} — ${phase.name}** (workshop ${ref.workshopId})`,
        ``,
        `Objectives:`,
        ...phase.objectives.map((o) => `- ${o}`),
      ].join("\n");
      return {
        kind: "text",
        heading: `@phase/${ref.workshopId}/${n}`,
        content,
      };
    }
    case "output": {
      const outputId = ref.outputId;
      if (!outputId) {
        return {
          kind: "metadata",
          heading: `@output/${ref.workshopId}`,
          note: "(missing output id)",
        };
      }
      const label = OUTPUT_LABELS[outputId];
      const resolved = resolveOutputContent(ref.workshopId, outputId);
      if (resolved) {
        const content = [`**${label}** — workshop ${ref.workshopId} output`, ``, resolved].join(
          "\n",
        );
        return {
          kind: "text",
          heading: `@output/${ref.workshopId}/${outputId}`,
          content,
        };
      }
      const description = OUTPUT_DESCRIPTIONS[outputId];
      const content = [
        `**${label}** — workshop ${ref.workshopId} output`,
        ``,
        description,
        ``,
        `_No authored text content is registered for this output in workshop ${ref.workshopId} yet._`,
      ].join("\n");
      return {
        kind: "text",
        heading: `@output/${ref.workshopId}/${outputId}`,
        content,
      };
    }
    case "asset": {
      // Resolve the live upload from the AssetLocker store. The store
      // is populated synchronously after hydration, so a snapshot read
      // is enough here.
      const state = useAssetLocker.getState();
      const list = state.entriesByWorkshop[ref.workshopId] ?? [];
      const found = ref.assetId
        ? list.find((u) => u.id === ref.assetId)
        : undefined;
      const name = found?.label ?? ref.assetName ?? ref.assetId ?? "(unknown)";
      const heading = `@asset/${ref.workshopId}/${ref.assetId ?? "unknown"}`;
      if (!found) {
        return {
          kind: "metadata",
          heading,
          note: `Referenced upload "${name}" is not present in the AssetLocker store (it may not be hydrated yet or has been removed).`,
        };
      }
      const content = [
        `**${found.label}** — uploaded asset in workshop ${ref.workshopId}`,
        ``,
        `- Colour: \`${found.color}\``,
        found.icon ? `- Icon: \`${found.icon}\`` : null,
        found.url ? `- Url: ${found.url}` : `- Url: _(not yet published)_`,
        `- Added by: ${found.addedBy || "_(unknown)_"}`,
        `- Added at: ${new Date(found.addedAt).toISOString()}`,
        ``,
        `_File content is not transcluded; treat this as metadata about a referenced upload._`,
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
      return { kind: "text", heading, content };
    }
    case "finding": {
      // AI-ANALYST — resolve against the live `useFindings` store, the same
      // snapshot-read pattern as `@asset`. The findings register (REG-FIND)
      // makes this a real transclusion; `assetId` carries the finding id,
      // `assetName` the cached title for chip render after a store reset.
      const list = useFindings.getState().entriesByWorkshop[ref.workshopId] ?? [];
      const found = ref.assetId ? list.find((f) => f.id === ref.assetId) : undefined;
      const name = found?.title ?? ref.assetName ?? ref.assetId ?? "(unknown)";
      const heading = `@finding/${ref.workshopId}/${ref.assetId ?? "unknown"}`;
      if (!found) {
        return {
          kind: "metadata",
          heading,
          note: `Referenced finding "${name}" is not present in the findings store (it may not be hydrated yet or has been removed).`,
        };
      }
      const content = [
        `**${found.title}** — finding in workshop ${ref.workshopId}`,
        ``,
        `- Severity: **${found.severity}**`,
        `- Domain: ${found.domain}`,
        `- Status: ${found.status}`,
        found.owner ? `- Owner: ${found.owner}` : null,
        found.linkedWorkshopIds.length
          ? `- Also spans: ${found.linkedWorkshopIds.join(", ")}`
          : null,
        found.detail ? `` : null,
        found.detail ? found.detail : null,
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
      return { kind: "text", heading, content };
    }
    case "decision": {
      const list = useDecisions.getState().entriesByWorkshop[ref.workshopId] ?? [];
      const found = ref.assetId ? list.find((d) => d.id === ref.assetId) : undefined;
      const name = found?.title ?? ref.assetName ?? ref.assetId ?? "(unknown)";
      const heading = `@decision/${ref.workshopId}/${ref.assetId ?? "unknown"}`;
      if (!found) {
        return {
          kind: "metadata",
          heading,
          note: `Referenced decision "${name}" is not present in the decisions store (it may not be hydrated yet or has been removed).`,
        };
      }
      const content = [
        `**${found.title}** — decision in workshop ${ref.workshopId}`,
        ``,
        `- Status: **${found.status}**`,
        found.owner ? `- Owner: ${found.owner}` : null,
        found.rationale ? `` : null,
        found.rationale ? found.rationale : null,
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
      return { kind: "text", heading, content };
    }
    case "action": {
      const list = useActions.getState().entriesByWorkshop[ref.workshopId] ?? [];
      const found = ref.assetId ? list.find((a) => a.id === ref.assetId) : undefined;
      const name = found?.title ?? ref.assetName ?? ref.assetId ?? "(unknown)";
      const heading = `@action/${ref.workshopId}/${ref.assetId ?? "unknown"}`;
      if (!found) {
        return {
          kind: "metadata",
          heading,
          note: `Referenced action "${name}" is not present in the actions store (it may not be hydrated yet or has been removed).`,
        };
      }
      const content = [
        `**${found.title}** — action in workshop ${ref.workshopId}`,
        ``,
        `- Status: **${found.status}**`,
        found.owner ? `- Owner: ${found.owner}` : null,
        found.due ? `- Due: ${found.due}` : null,
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
      return { kind: "text", heading, content };
    }
    case "assumption": {
      const list = useAssumptions.getState().entriesByWorkshop[ref.workshopId] ?? [];
      const found = ref.assetId ? list.find((a) => a.id === ref.assetId) : undefined;
      const name = found?.statement ?? ref.assetName ?? ref.assetId ?? "(unknown)";
      const heading = `@assumption/${ref.workshopId}/${ref.assetId ?? "unknown"}`;
      if (!found) {
        return {
          kind: "metadata",
          heading,
          note: `Referenced assumption "${name}" is not present in the assumptions store (it may not be hydrated yet or has been removed).`,
        };
      }
      const content = [
        `**${found.statement}** — assumption in workshop ${ref.workshopId}`,
        ``,
        `- Status: **${found.status}**`,
        found.owner ? `- Owner: ${found.owner}` : null,
        found.basis ? `` : null,
        found.basis ? found.basis : null,
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
      return { kind: "text", heading, content };
    }
    case "opportunity": {
      const list =
        useOpportunities.getState().entriesByWorkshop[ref.workshopId] ?? [];
      const found = ref.assetId ? list.find((o) => o.id === ref.assetId) : undefined;
      const name = found?.title ?? ref.assetName ?? ref.assetId ?? "(unknown)";
      const heading = `@opportunity/${ref.workshopId}/${ref.assetId ?? "unknown"}`;
      if (!found) {
        return {
          kind: "metadata",
          heading,
          note: `Referenced opportunity "${name}" is not present in the opportunities store (it may not be hydrated yet or has been removed).`,
        };
      }
      const { body: detailBody, truncated } = formatBodyContent(found.detail);
      const content = [
        `**${found.title}** — opportunity in workshop ${ref.workshopId}`,
        ``,
        `- Status: **${found.status}**`,
        found.owner ? `- Owner: ${found.owner}` : null,
        found.detail ? `` : null,
        found.detail ? detailBody : null,
        truncated
          ? `_Body truncated; this reference carries the first ${BODY_FULL_MAX} characters._`
          : null,
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
      return { kind: "text", heading, content };
    }
    case "recommendation": {
      const list =
        useRecommendations.getState().entriesByWorkshop[ref.workshopId] ?? [];
      const found = ref.assetId ? list.find((r) => r.id === ref.assetId) : undefined;
      const name = found?.title ?? ref.assetName ?? ref.assetId ?? "(unknown)";
      const heading = `@recommendation/${ref.workshopId}/${ref.assetId ?? "unknown"}`;
      if (!found) {
        return {
          kind: "metadata",
          heading,
          note: `Referenced recommendation "${name}" is not present in the recommendations store (it may not be hydrated yet or has been removed).`,
        };
      }
      const { body: recBody, truncated } = formatBodyContent(found.body);
      const content = [
        `**${found.title}** — recommendation in workshop ${ref.workshopId}`,
        ``,
        `- Status: **${found.status}**`,
        found.owner ? `- Owner: ${found.owner}` : null,
        found.body ? `` : null,
        found.body ? recBody : null,
        truncated
          ? `_Body truncated; this reference carries the first ${BODY_FULL_MAX} characters._`
          : null,
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
      return { kind: "text", heading, content };
    }
    case "note": {
      const list =
        useFacilitationNotes.getState().entriesByWorkshop[ref.workshopId] ?? [];
      const found = ref.assetId ? list.find((n) => n.id === ref.assetId) : undefined;
      const name = found?.body
        ? found.body.slice(0, 80)
        : (ref.assetName ?? ref.assetId ?? "(unknown)");
      const heading = `@note/${ref.workshopId}/${ref.assetId ?? "unknown"}`;
      if (!found) {
        return {
          kind: "metadata",
          heading,
          note: `Referenced note "${name}" is not present in the facilitation notes store (it may not be hydrated yet or has been removed).`,
        };
      }
      const { body: noteBody, truncated } = formatBodyContent(found.body);
      const content = [
        `**Facilitation note** — workshop ${ref.workshopId}`,
        ``,
        noteBody,
        truncated
          ? `_Body truncated; this reference carries the first ${BODY_FULL_MAX} characters._`
          : null,
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
      return { kind: "text", heading, content };
    }
    case "transcript": {
      const list = useTranscripts.getState().entriesByWorkshop[ref.workshopId] ?? [];
      const found = ref.assetId ? list.find((t) => t.id === ref.assetId) : undefined;
      const name = found?.title ?? ref.assetName ?? ref.assetId ?? "(unknown)";
      const heading = `@transcript/${ref.workshopId}/${ref.assetId ?? "unknown"}`;
      if (!found) {
        return {
          kind: "metadata",
          heading,
          note: `Referenced transcript "${name}" is not present in the transcripts store (it may not be hydrated yet or has been removed).`,
        };
      }
      const { body: transcriptBody, truncated } = formatBodyContent(found.body);
      const content = [
        `**${found.title}** — transcript in workshop ${ref.workshopId}`,
        ``,
        found.body ? transcriptBody : `_(empty body)_`,
        found.mediaName ? `- Media: ${found.mediaName}` : null,
        truncated
          ? `_Body truncated; this reference carries the first ${BODY_FULL_MAX} characters._`
          : null,
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
      return { kind: "text", heading, content };
    }
    case "briefing": {
      const text = formatPreReading(ref.workshopId);
      if (!text) {
        return {
          kind: "metadata",
          heading: `@briefing/${ref.workshopId}`,
          note: `(no pre-reading dossier registered for ${ref.workshopId})`,
        };
      }
      return {
        kind: "text",
        heading: `@briefing/${ref.workshopId}`,
        content: text,
      };
    }
    case "questionnaire": {
      const text = formatQuestionnaire(ref.workshopId);
      if (!text) {
        return {
          kind: "metadata",
          heading: `@questionnaire/${ref.workshopId}`,
          note: `(no questionnaire registered for ${ref.workshopId})`,
        };
      }
      return {
        kind: "text",
        heading: `@questionnaire/${ref.workshopId}`,
        content: text,
      };
    }
    default: {
      // Exhaustive guard — narrows `ref.kind` to never.
      const _exhaustive: never = ref.kind;
      void _exhaustive;
      return {
        kind: "metadata",
        heading: "@unknown",
        note: "unknown reference kind",
      };
    }
  }
}

/** Resolve an array of references in parallel. */
export async function resolveReferences(
  refs: XaesReference[],
): Promise<ResolvedReference[]> {
  return Promise.all(refs.map(resolveReference));
}

/**
 * Wrap a resolved reference as an `<reference name="…">…</reference>`
 * block. Markdown content goes through unchanged; metadata blocks
 * become a one-liner. Matches the trust-boundary contract documented
 * in `src/content/xaes-system-prompt.ts`.
 */
export function renderReferenceBlock(r: ResolvedReference): string {
  if (r.kind === "text") {
    return `<reference name="${r.heading}">\n${r.content}\n</reference>`;
  }
  return `<reference name="${r.heading}">\n${r.note}\n</reference>`;
}

/** Canonical output card metadata for picker UIs. */
export const WORKSHOP_OUTPUTS: ReadonlyArray<{
  id: WorkshopOutputId;
  label: string;
}> = (Object.keys(OUTPUT_LABELS) as WorkshopOutputId[]).map((id) => ({
  id,
  label: OUTPUT_LABELS[id],
}));

/**
 * Workshops whose register rows (decisions, actions, assumptions,
 * findings) appear in `@`-mention picker candidates for `scope`.
 * MC lists all eight; a workshop scope lists that workshop plus every
 * prior one so users can pin e.g. a WS2 decision while chatting in WS4.
 */
function registerWorkshopsForScope(
  scope: Workshop["id"] | "MC",
): Workshop[] {
  if (scope === "MC") return WORKSHOPS;
  const ws = WORKSHOPS.find((w) => w.id === scope);
  if (!ws) return [];
  return WORKSHOPS.filter((w) => w.num <= ws.num);
}

/**
 * Convenience: enumerate the picker candidates. Used by both the
 * inline `@`-mention popover and the context-bar dropdown.
 *
 * The function reads the `useAssetLocker` snapshot synchronously so
 * curated uploads appear in the picker. If the store hasn't hydrated
 * yet only the static workshop / phase / output candidates show up;
 * callers don't need to await hydration.
 */
export function listReferenceCandidates(
  scope: Workshop["id"] | "MC",
): XaesReference[] {
  const out: XaesReference[] = [];
  let workshops: Workshop[];
  if (scope === "MC") {
    workshops = WORKSHOPS;
  } else {
    const ws = WORKSHOPS.find((w) => w.id === scope);
    workshops = ws ? [ws] : [];
  }
  const lockerState = useAssetLocker.getState();
  const registerWorkshops = registerWorkshopsForScope(scope);
  for (const ws of workshops) {
    if (!ws) continue;
    out.push({
      kind: "workshop",
      workshopId: ws.id,
      label: workshopRefLabel(ws),
    });
    out.push({
      kind: "crew",
      workshopId: ws.id,
      label: `${ws.id} — Meet the Crew`,
    });
    const phases = getEffectiveWorkshopPhases(ws.id);
    for (let n = 1; n <= phases.length; n++) {
      const phaseName = phases[n - 1]?.name ?? `phase ${n}`;
      out.push({
        kind: "phase",
        workshopId: ws.id,
        phase: n,
        label: `${ws.id} — phase ${n}: ${phaseName}`,
      });
    }
    out.push({
      kind: "briefing",
      workshopId: ws.id,
      label: `${ws.id} — Pre-reading dossier`,
    });
    out.push({
      kind: "questionnaire",
      workshopId: ws.id,
      label: `${ws.id} — Questionnaire`,
    });
    for (const o of WORKSHOP_OUTPUTS) {
      out.push({
        kind: "output",
        workshopId: ws.id,
        outputId: o.id,
        label: `${ws.id} — ${o.label}`,
      });
    }
    const uploads = lockerState.entriesByWorkshop[ws.id] ?? [];
    for (const u of uploads) {
      out.push({
        kind: "asset",
        workshopId: ws.id,
        assetId: u.id,
        assetName: u.label,
        label: `${ws.id} — ${u.label}`,
      });
    }
  }
  for (const ws of registerWorkshops) {
    const decisions = useDecisions.getState().entriesByWorkshop[ws.id] ?? [];
    for (const d of decisions) {
      out.push({
        kind: "decision",
        workshopId: ws.id,
        assetId: d.id,
        assetName: d.title,
        label: `${ws.id} — ${d.title}`,
      });
    }
    const actions = useActions.getState().entriesByWorkshop[ws.id] ?? [];
    for (const a of actions) {
      out.push({
        kind: "action",
        workshopId: ws.id,
        assetId: a.id,
        assetName: a.title,
        label: `${ws.id} — ${a.title}`,
      });
    }
    const assumptions = useAssumptions.getState().entriesByWorkshop[ws.id] ?? [];
    for (const a of assumptions) {
      out.push({
        kind: "assumption",
        workshopId: ws.id,
        assetId: a.id,
        assetName: a.statement,
        label: `${ws.id} — ${a.statement}`,
      });
    }
    const findings = useFindings.getState().entriesByWorkshop[ws.id] ?? [];
    for (const f of findings) {
      out.push({
        kind: "finding",
        workshopId: ws.id,
        assetId: f.id,
        assetName: f.title,
        label: `${ws.id} — ${f.title}`,
      });
    }
    const opportunities = useOpportunities.getState().entriesByWorkshop[ws.id] ?? [];
    for (const o of opportunities) {
      out.push({
        kind: "opportunity",
        workshopId: ws.id,
        assetId: o.id,
        assetName: o.title,
        label: `${ws.id} — ${o.title}`,
      });
    }
    const recommendations =
      useRecommendations.getState().entriesByWorkshop[ws.id] ?? [];
    for (const r of recommendations) {
      out.push({
        kind: "recommendation",
        workshopId: ws.id,
        assetId: r.id,
        assetName: r.title,
        label: `${ws.id} — ${r.title}`,
      });
    }
    const notes = useFacilitationNotes.getState().entriesByWorkshop[ws.id] ?? [];
    for (const n of notes) {
      const preview = n.body.length > 60 ? `${n.body.slice(0, 60).trim()}…` : n.body;
      out.push({
        kind: "note",
        workshopId: ws.id,
        assetId: n.id,
        assetName: preview,
        label: `${ws.id} — ${preview}`,
      });
    }
    const transcripts = useTranscripts.getState().entriesByWorkshop[ws.id] ?? [];
    for (const t of transcripts) {
      out.push({
        kind: "transcript",
        workshopId: ws.id,
        assetId: t.id,
        assetName: t.title,
        label: `${ws.id} — ${t.title}`,
      });
    }
  }
  return out;
}
